import React, { useRef, useState } from 'react';
import { createDebt, getEmprestimosDoBanco, SugestaoDeDividaDoBanco } from '../services/api';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import { brlOrMasked } from '../utils/format';
import { useBotaoOcupado } from '../utils/botaoOcupado';

/**
 * Os empréstimos que o banco mostra, como sugestão de dívida.
 *
 * ── Sugestão, e não cadastro ─────────────────────────────────────────────
 *
 * O banco sabe as parcelas, o valor, a primeira data e a taxa. Não sabe a
 * única coisa que o app NÃO pode adivinhar: se a parcela sai do salário antes
 * de ele cair na conta. Marcado errado, a projeção erra uma parcela inteira
 * todo mês (`services/debt.ts`). Então para consignado a resposta vem pronta
 * (é o que a palavra significa), e para os outros são dois botões: a pergunta
 * É o botão de cadastrar.
 *
 * ── Só quando ela pede ────────────────────────────────────────────────────
 *
 * Um botão, e não uma busca ao abrir a seção: são duas chamadas ao provedor
 * por banco, e contrato de empréstimo não muda todo dia.
 *
 * ── O saldo devedor é dito como informação do BANCO ──────────────────────
 *
 * O app sempre disse não saber esse número, e continua calculando "falta
 * pagar" do jeito dele, que embute os juros futuros. Os dois não são a mesma
 * coisa, e por isso a frase diz de quem é cada um.
 */
interface Props {
  hideValues?: boolean;
  /** Uma dívida foi cadastrada: a seção recarrega e a projeção reconta. */
  onCadastrou: () => void;
}

const dataLegivel = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

const EmprestimosDoBanco: React.FC<Props> = ({ hideValues = false, onCadastrou }) => {
  const [sugestoes, setSugestoes] = useState<SugestaoDeDividaDoBanco[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState<string | null>(null);
  // "Da minha conta" e "Direto do salário" dividem o mesmo estado: quem foi tocado se enche.
  const caixa = useRef<HTMLDivElement>(null);
  useBotaoOcupado(caixa, salvando !== null);
  const [erro, setErro] = useState('');
  /** Os bancos que não responderam: deles não dá para dizer "nenhum empréstimo". */
  const [naoLidos, setNaoLidos] = useState<string[]>([]);
  const fmt = (v: number) => brlOrMasked(v, hideValues);

  const buscar = async () => {
    setBuscando(true); setErro(''); setNaoLidos([]);
    try {
      const r = await getEmprestimosDoBanco();
      setNaoLidos(r.naoLidos);
      // Nenhum banco respondeu: não há lista para mostrar, só o aviso.
      setSugestoes(r.sugestoes.length === 0 && r.naoLidos.length > 0 ? null : r.sugestoes);
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui ler os empréstimos no banco.'));
    } finally {
      setBuscando(false);
    }
  };

  const cadastrar = async (s: SugestaoDeDividaDoBanco, descontaEmFolha: boolean) => {
    setSalvando(s.idExterno); setErro('');
    try {
      await createDebt({
        name: s.nome,
        type: s.tipo,
        installments: s.parcelas,
        installmentValue: s.valorDaParcela,
        firstInstallmentDate: s.primeiraParcela,
        deductedFromSalary: descontaEmFolha,
        monthlyRate: s.taxaMensal,
      });
      // Sai da lista na hora: o servidor só saberia que ela existe na próxima busca.
      setSugestoes(atual => (atual ?? []).map(x => (x.idExterno === s.idExterno ? { ...x, jaCadastrada: true } : x)));
      onCadastrou();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui cadastrar a dívida.'));
    } finally {
      setSalvando(null);
    }
  };

  const novas = (sugestoes ?? []).filter(s => !s.jaCadastrada);

  return (
    <div className="dbt-banco" ref={caixa}>
      {sugestoes === null ? (
        <button type="button" className="dbt-banco-buscar" onClick={buscar} disabled={buscando} aria-busy={buscando}>
          Buscar empréstimos no meu banco
        </button>
      ) : novas.length === 0 ? (
        <p className="dbt-banco-nota">
          {sugestoes.length === 0
            ? 'O banco não mostra nenhum empréstimo em aberto.'
            : 'Os empréstimos que o banco mostra já estão nas suas dívidas.'}
        </p>
      ) : (
        novas.map(s => (
          <div className="dbt-banco-item" key={s.idExterno}>
            <strong>{s.nome}</strong>
            <p className="dbt-banco-linha">
              {s.parcelas}x de {fmt(s.valorDaParcela)} · primeira em {dataLegivel(s.primeiraParcela)}
              {s.taxaMensal !== null && <> · {s.taxaMensal.toLocaleString('pt-BR')}% ao mês</>}
            </p>
            {s.saldoDevedorNoBanco !== null && (
              <p className="dbt-banco-nota">
                Segundo o banco, o saldo devedor hoje é {fmt(s.saldoDevedorNoBanco)}.
              </p>
            )}
            {s.descontaEmFolha ? (
              <div className="dbt-banco-acoes">
                <span className="dbt-banco-nota">Consignado: sai direto do salário.</span>
                <button type="button" className="dbt-banco-btn principal" onClick={() => cadastrar(s, true)} disabled={salvando === s.idExterno}>
                  Cadastrar
                </button>
              </div>
            ) : (
              <>
                <p className="dbt-banco-pergunta">De onde sai a parcela?</p>
                <div className="dbt-banco-acoes">
                  <button type="button" className="dbt-banco-btn" onClick={() => cadastrar(s, false)} disabled={salvando === s.idExterno}>
                    Da minha conta
                  </button>
                  <button type="button" className="dbt-banco-btn" onClick={() => cadastrar(s, true)} disabled={salvando === s.idExterno}>
                    Direto do salário
                  </button>
                </div>
              </>
            )}
          </div>
        ))
      )}
      {naoLidos.length > 0 && (
        <p className="dbt-banco-erro" role="alert">
          <span>
            {naoLidos.length === 1 ? `O ${naoLidos[0]} não respondeu` : `${naoLidos.join(', ')} não responderam`} agora.
            Tente de novo em alguns minutos.
          </span>
          <button type="button" className="dbt-banco-dispensar" onClick={() => setNaoLidos([])} aria-label="Fechar esta mensagem">✕</button>
        </p>
      )}
      {erro && (
        <p className="dbt-banco-erro" role="alert">
          <span>{erro}</span>
          <button type="button" className="dbt-banco-dispensar" onClick={() => setErro('')} aria-label="Fechar esta mensagem">✕</button>
        </p>
      )}
    </div>
  );
};

export default EmprestimosDoBanco;
